import flattenDeep from 'lodash/flattenDeep';
import addressFields, { DEFAULT_REQUIRED_FIELDS } from '../constants/addressFields';
import {
  ADDRESS_FORM_FIELDS_MAPPING,
  PICKUP_FORM_FIELD_MAPPINGS,
} from '../constants/defaultSettings';

/* eslint-disable import/prefer-default-export */
export const isAddressValid = (shippingAddress, countryCode) => {
  const requiredFields = countryCode && addressFields[countryCode].validation 
    ? addressFields[countryCode].validation 
    : DEFAULT_REQUIRED_FIELDS;

  return requiredFields.every(field => Boolean(shippingAddress[field]));
};

const TEMPLATE_PATTERN = /({.?.?\w+.?.?})/g;
const NOT_ALPHANUMERIC = /[^a-z0-9]/gi;

export const renderAddressTemplate = ({ address, template }) => {
  const replacementKeys = template.match(TEMPLATE_PATTERN);
  if (!replacementKeys) return '';

  const renderedTemplate = replacementKeys.reduce((acc, key) => {
    const addressKey = key.replace(NOT_ALPHANUMERIC, '');
    const replacementValue = address[addressKey]
      ? key.replace('{', '').replace('}', '').replace(addressKey, address[addressKey])
      : '';
    return acc.replace(key, replacementValue);
  }, template);

  return renderedTemplate.replace(/,\s*$/, '');
};

export const formatAddressSingleLine = ({ countryCode, address }) => {
  const addressFormat = (addressFields[countryCode] || addressFields.US).single;
  if (!addressFormat) return '';
  return renderAddressTemplate({ address, template: addressFormat });
};

export const formatAddressMultiLine = ({ countryCode, address }) => {
  const addressFormat = (addressFields[countryCode] || addressFields.US).multi;
  if (!addressFormat) return '';
  return addressFormat
    .map((template) => renderAddressTemplate({ address, template }))
    .filter(Boolean);
};

const mapFieldsConfig = (fields = []) =>
  fields.map((field) => {
    const key = field.replace('{', '').replace('}', '');
    return {
      ...{
        addressField: key,
        type: 'text',
        key,
        required: true,
        width: 100,
      },
      ...ADDRESS_FORM_FIELDS_MAPPING[key],
      width: 100 / fields.length,
    };
  });

export const buildAddressFields = ({ countryCode, replaceFields = {} }) => {
  const addressFormat = (addressFields[countryCode] || addressFields.US).multi;
  const updatedFormat = addressFormat.map((field) => replaceFields[field] || field);
  const formConfig = updatedFormat.map((fields) => {
    const splitFields = fields.match(TEMPLATE_PATTERN);
    return mapFieldsConfig(splitFields);
  });
  return flattenDeep(formConfig);
};

export const buildPickupAddressFields = ({ country, replaceFields = {} }) => {
  const addressFormat = (addressFields[country] || addressFields.US).multi;
  const updatedFormat = addressFormat.map((field) => replaceFields[field] || field);
  const formConfig = updatedFormat.map((fields) => {
    const splitFields = fields.match(TEMPLATE_PATTERN);
    return splitFields.map((field) => {
      const key = field.replace('{', '').replace('}', '');
      return {
        ...{
          type: 'text',
          key,
          required: true,
        },
        ...PICKUP_FORM_FIELD_MAPPINGS[key],
        width: 100,
      };
    });
  });
  return flattenDeep(formConfig);
};

export const buildPickupFieldsFromFormat = (layout) => {
  if (!layout) return undefined;
  return layout.map(
    ({
      name: key,
      type,
      display_title: title,
      required,
      pattern,
      allowed_values: allowedValues,
    }) => {
      let inputType = 'text';
      const lowercaseType = typeof type === 'string' ? type.toLowerCase() : '';

      switch (lowercaseType) {
        case 'enum':
          inputType = 'select';
          break;
        case 'number':
          inputType = 'number';
          break;
        case 'free_text':
        default:
          inputType = 'text';
          break;
      }

      // Use the localized country dropdown since the API does not send back localized options
      if (key === 'country') {
        return {
          key,
          title,
          type: 'select',
          languageKey: `address_form.input.${key}`,
          required,
          width: 100,
          pattern,
          allowedValues,
        };
      }

      return {
        key,
        title,
        type: inputType,
        languageKey: `address_form.input.${key}`,
        required,
        width: 100,
        pattern,
        allowedValues,
      };
    },
  );
};
